/* Apple-style store layer: reveal on scroll, sub-nav, highlights carousel, buy-page gallery + variant tiles. */
(function () {
  document.documentElement.classList.add('apl-js');

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root) {
    const els = (root || document).querySelectorAll('[data-apl-reveal]:not(.is-in)');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Sub-nav ---------- */
  class AplSubnav extends HTMLElement {
    connectedCallback() {
      const nav = this.querySelector('.apl-subnav');
      const btn = this.querySelector('.apl-subnav__toggle');
      if (!btn || !nav) return;
      btn.addEventListener('click', () => {
        const open = nav.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open);
      });
      const bag = this.querySelector('[data-apl-bag]');
      if (bag) {
        bag.addEventListener('click', (e) => {
          const drawer = document.querySelector('cart-drawer');
          if (!drawer || typeof drawer.open !== 'function') return;
          e.preventDefault();
          drawer.open(bag);
        });
        const count = bag.querySelector('[data-apl-bag-count]');
        const refresh = () =>
          fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js')
            .then((r) => r.json())
            .then((c) => {
              count.textContent = c.item_count;
              count.hidden = c.item_count === 0;
              bag.setAttribute('aria-label', 'Bag, ' + c.item_count + ' items');
            })
            .catch(() => {});
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
      }
      const here = new URL(window.location.href);
      const view = here.searchParams.get('view') || '';
      const path = here.pathname.replace(/\/$/, '') || '/';
      this.querySelectorAll('.apl-subnav__menu a').forEach((a) => {
        const u = new URL(a.getAttribute('href') || '/', here.origin);
        const hp = u.pathname.replace(/\/$/, '') || '/';
        if (hp === path && (u.searchParams.get('view') || '') === view) a.setAttribute('aria-current', 'page');
      });
    }
  }
  if (!customElements.get('apl-subnav')) customElements.define('apl-subnav', AplSubnav);

  /* ---------- Highlights carousel ---------- */
  class AplCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      if (!this.track || !this.cards.length) return;

      this.dots.forEach((d, i) => d.addEventListener('click', () => this.goTo(i)));
      this.prev && this.prev.addEventListener('click', () => this.goTo(this.index - 1));
      this.next && this.next.addEventListener('click', () => this.goTo(this.index + 1));
      this.index = 0;
      let t;
      this.track.addEventListener('scroll', () => {
        clearTimeout(t);
        t = setTimeout(() => this.sync(), 60);
      }, { passive: true });
      this.sync();
      this.watchScenes();
      this.setupAutoplay();
    }
    // Starts each card's scene animation (and any $ counter) the first time it is mostly visible.
    watchScenes() {
      if (!('IntersectionObserver' in window)) { this.cards.forEach((c) => c.classList.add('is-in')); return; }
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting || e.target.classList.contains('is-in')) return;
          e.target.classList.add('is-in');
          e.target.querySelectorAll('[data-count-from]').forEach((el) => this.count(el));
        });
      }, { threshold: 0.6 });
      this.cards.forEach((c) => io.observe(c));
    }
    count(el) {
      const from = Number(el.dataset.countFrom), to = Number(el.dataset.countTo || 0);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { el.textContent = to; return; }
      const start = performance.now(), dur = 1400;
      const tick = (now) => {
        const p = Math.min(1, (now - start) / dur);
        el.textContent = Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3)));
        if (p < 1) requestAnimationFrame(tick);
      };
      el.textContent = from;
      requestAnimationFrame(tick);
    }
    // Auto-advance while the carousel is on screen; stops for good once the shopper takes over.
    setupAutoplay() {
      const secs = Number(this.dataset.autoplay);
      if (!secs || this.cards.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.pauseBtn = this.querySelector('[data-pause]');
      this.userPaused = false;
      this.pauseBtn && this.pauseBtn.addEventListener('click', () => {
        this.userPaused = this.classList.contains('is-playing');
        this.userPaused ? this.stop() : this.play();
      });
      const takeOver = () => { this.userPaused = true; this.stop(); };
      this.track.addEventListener('pointerdown', takeOver, { passive: true });
      this.track.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) takeOver(); }, { passive: true });
      [this.prev, this.next, ...this.dots].forEach((b) => b && b.addEventListener('click', takeOver));
      this.secs = secs;
      new IntersectionObserver(([e]) => {
        if (e.isIntersecting && !this.userPaused) this.play(); else this.stop();
      }, { threshold: 0.4 }).observe(this.track);
    }
    play() {
      this.stop();
      this.classList.add('is-playing');
      if (this.pauseBtn) this.pauseBtn.setAttribute('aria-label', 'Pause');
      this.timer = setInterval(() => this.goTo(this.index >= this.cards.length - 1 ? 0 : this.index + 1), this.secs * 1000);
    }
    stop() {
      clearInterval(this.timer);
      this.classList.remove('is-playing');
      if (this.pauseBtn) this.pauseBtn.setAttribute('aria-label', 'Play');
    }
    goTo(i) {
      i = Math.max(0, Math.min(this.cards.length - 1, i));
      const card = this.cards[i];
      const left = card.offsetLeft - (this.track.clientWidth - card.clientWidth) / 2;
      this.track.scrollTo({ left, behavior: 'smooth' });
    }
    sync() {
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let best = 0, bestDist = Infinity;
      this.cards.forEach((c, i) => {
        const d = Math.abs(c.offsetLeft + c.clientWidth / 2 - center);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      this.index = best;
      this.dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === best);
        d.setAttribute('aria-current', i === best ? 'true' : 'false');
      });
      if (this.prev) this.prev.disabled = best === 0;
      if (this.next) this.next.disabled = best === this.cards.length - 1;
    }
  }
  if (!customElements.get('apl-carousel')) customElements.define('apl-carousel', AplCarousel);

  /* ---------- Highlights film (section apl-film) ----------
     Scenes carry data-at / data-len (ms). The clock only advances while playing and on screen. */
  class AplFilm extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.scenes = Array.from(this.querySelectorAll('.af-scene')).map((el) => ({ el, at: +el.dataset.at, end: +el.dataset.at + +el.dataset.len }));
      if (!this.scenes.length) return;
      this.total = Math.max(...this.scenes.map((s) => s.end)) + 350;
      this.bar = this.querySelector('[data-bar]');
      this.t = 0;
      this.visible = false;
      this.userPaused = false;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const still = this.scenes.find((s) => 'still' in s.el.dataset) || this.scenes[0];
        this.classList.add('is-still', 'is-paused');
        still.el.classList.add('on');
        const toggle = this.querySelector('[data-toggle]');
        if (toggle) toggle.hidden = true;
        return;
      }
      this.querySelector('[data-toggle]').addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.updateState();
      });
      new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; this.updateState(); }, { threshold: 0.35 }).observe(this);
      this.updateState();
    }
    updateState() {
      const playing = this.visible && !this.userPaused;
      this.classList.toggle('is-paused', !playing);
      const toggle = this.querySelector('[data-toggle]');
      if (toggle) toggle.setAttribute('aria-label', this.userPaused ? 'Play' : 'Pause');
      if (playing && !this.raf) { this.last = performance.now(); this.raf = requestAnimationFrame((n) => this.frame(n)); }
      if (!playing && this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
    }
    frame(now) {
      const prev = this.t;
      this.t = (this.t + Math.min(100, now - this.last)) % this.total;
      this.last = now;
      if (this.t < prev) this.scenes.forEach((s) => s.el.classList.remove('on'));
      this.scenes.forEach((s) => {
        const active = this.t >= s.at && this.t < s.end;
        if (active && !s.el.classList.contains('on')) this.enter(s);
        else if (!active && s.el.classList.contains('on')) s.el.classList.remove('on');
      });
      if (this.bar) this.bar.style.width = (this.t / this.total * 100) + '%';
      this.raf = requestAnimationFrame((n) => this.frame(n));
    }
    enter(s) {
      s.el.classList.add('on');
      s.el.querySelectorAll('[data-count]').forEach((n) => {
        const from = +n.dataset.count, t0 = performance.now(), dur = 900;
        const tick = (now) => {
          const p = Math.min(1, (now - t0) / dur);
          n.textContent = Math.round(from * Math.pow(1 - p, 3));
          if (p < 1 && s.el.classList.contains('on')) requestAnimationFrame(tick);
        };
        n.textContent = from;
        requestAnimationFrame(tick);
      });
    }
  }
  if (!customElements.get('apl-film')) customElements.define('apl-film', AplFilm);

  /* ---------- Buy page gallery ---------- */
  class AplGallery extends HTMLElement {
    connectedCallback() {
      // allSlides includes option-only photos (data-variant-only); slides is the browsable photo roll.
      this.allSlides = Array.from(this.querySelectorAll('[data-slide]'));
      this.slides = this.allSlides.filter((s) => !s.hasAttribute('data-variant-only'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.show(i)));
      const p = this.querySelector('[data-prev]');
      const n = this.querySelector('[data-next]');
      p && p.addEventListener('click', () => this.step(-1));
      n && n.addEventListener('click', () => this.step(1));
      this.index = 0;

      let x0 = null;
      const stage = this.querySelector('.apl-gallery__stage');
      stage && stage.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      stage && stage.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1);
        x0 = null;
      });

      // Click the main image to open a full-screen viewer.
      if (stage && this.allSlides.some((sl) => sl.querySelector('img'))) {
        stage.classList.add('is-zoomable');
        stage.addEventListener('click', (e) => {
          if (e.target.closest('button, a, video, model-viewer, iframe')) return;
          const cur = this.current();
          if (!cur || !cur.querySelector('img')) return;
          this.openViewer();
        });
      }
    }
    current() {
      return this.allSlides.find((s) => s.classList.contains('is-active'));
    }
    step(d) {
      // From an option-only photo, next goes to the first roll photo and previous to the last.
      if (this.index < 0) this.show(d > 0 ? 0 : this.slides.length - 1);
      else this.show(this.index + d);
    }
    fullSrc(img) {
      const set = (img.getAttribute('srcset') || '').split(',').map((p) => p.trim().split(' ')[0]).filter(Boolean);
      return set.length ? set[set.length - 1] : img.currentSrc || img.src;
    }
    openViewer() {
      const items = this.slides.map((sl) => sl.querySelector('img')).filter(Boolean);
      const cur = this.current();
      const startImg = cur && cur.querySelector('img');
      if (startImg && !items.includes(startImg)) items.unshift(startImg);
      if (!items.length) return;
      let i = Math.max(0, items.indexOf(startImg));
      const box = document.createElement('div');
      box.className = 'apl-viewer';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', 'Product images');
      box.innerHTML =
        '<button type="button" class="apl-viewer__close" aria-label="Close">&times;</button>' +
        '<button type="button" class="apl-viewer__arrow apl-viewer__arrow--prev" aria-label="Previous image"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
        '<figure class="apl-viewer__stage"><img alt=""></figure>' +
        '<button type="button" class="apl-viewer__arrow apl-viewer__arrow--next" aria-label="Next image"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
        '<p class="apl-viewer__count" aria-live="polite"></p>';
      const view = box.querySelector('.apl-viewer__stage img');
      const count = box.querySelector('.apl-viewer__count');
      const go = (k) => {
        i = (k + items.length) % items.length;
        view.src = this.fullSrc(items[i]);
        view.alt = items[i].alt || '';
        count.textContent = (i + 1) + ' / ' + items.length;
      };
      const lastFocus = document.activeElement;
      const close = () => {
        document.removeEventListener('keydown', onKey);
        box.remove();
        document.documentElement.style.overflow = '';
        const k = this.slides.findIndex((sl) => sl.querySelector('img') === items[i]);
        if (k > -1) this.show(k);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      };
      const onKey = (e) => {
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') go(i - 1);
        else if (e.key === 'ArrowRight') go(i + 1);
      };
      box.querySelector('.apl-viewer__close').addEventListener('click', close);
      box.querySelector('.apl-viewer__arrow--prev').addEventListener('click', () => go(i - 1));
      box.querySelector('.apl-viewer__arrow--next').addEventListener('click', () => go(i + 1));
      box.addEventListener('click', (e) => { if (e.target === box || e.target.classList.contains('apl-viewer__stage')) close(); });
      let tx = null;
      box.addEventListener('touchstart', (e) => { tx = e.touches[0].clientX; }, { passive: true });
      box.addEventListener('touchend', (e) => {
        if (tx === null) return;
        const dx = e.changedTouches[0].clientX - tx;
        if (Math.abs(dx) > 40) go(i + (dx < 0 ? 1 : -1));
        tx = null;
      });
      document.addEventListener('keydown', onKey);
      if (items.length < 2) box.classList.add('is-single');
      document.body.appendChild(box);
      document.documentElement.style.overflow = 'hidden';
      go(i);
      box.querySelector('.apl-viewer__close').focus();
    }
    show(i) {
      if (!this.slides.length) return;
      const len = this.slides.length;
      this.index = (i + len) % len;
      const active = this.slides[this.index];
      this.allSlides.forEach((s) => s.classList.toggle('is-active', s === active));
      this.thumbs.forEach((t, k) => {
        t.classList.toggle('is-active', k === this.index);
        t.setAttribute('aria-current', k === this.index ? 'true' : 'false');
      });
    }
    showMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) { this.show(i); return; }
      // Option-only photo: show it without adding it to the roll.
      const extra = this.allSlides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!extra) return;
      this.index = -1;
      this.allSlides.forEach((s) => s.classList.toggle('is-active', s === extra));
      this.thumbs.forEach((t) => { t.classList.remove('is-active'); t.setAttribute('aria-current', 'false'); });
    }
  }
  if (!customElements.get('apl-gallery')) customElements.define('apl-gallery', AplGallery);

  /* ---------- Variant tiles ---------- */
  class AplVariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[type="application/json"]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.section = this.closest('[data-apl-buy]');
      this.addEventListener('change', () => this.update());
    }
    selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset')).map((fs) => {
        const c = fs.querySelector('input:checked');
        return c ? c.value : null;
      });
    }
    update() {
      const opts = this.selectedOptions();
      const v = this.data.variants.find((x) => x.options.every((o, i) => o === opts[i]));
      const s = this.section;
      const idInput = s.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
      const btn = s.querySelector('[data-apl-add]');
      const btnText = btn && btn.querySelector('[data-apl-add-text]');
      const priceEls = s.querySelectorAll('[data-apl-price]');
      const compareEls = s.querySelectorAll('[data-apl-compare]');
      const selLabels = s.querySelectorAll('[data-apl-selected]');

      selLabels.forEach((el) => { el.textContent = opts.filter(Boolean).join(' · '); });

      if (!v) {
        if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.disabled = true; }
        if (btnText) btnText.textContent = this.data.strings.unavailable;
        return;
      }
      if (idInput) idInput.value = v.id;
      priceEls.forEach((el) => { el.textContent = this.money(v.price); });
      compareEls.forEach((el) => {
        el.textContent = v.compare_at_price > v.price ? this.money(v.compare_at_price) : '';
        el.hidden = !(v.compare_at_price > v.price);
      });
      if (btn) {
        btn.disabled = !v.available;
        btn.toggleAttribute('aria-disabled', !v.available);
        if (!v.available) btn.setAttribute('aria-disabled', 'true');
        if (btnText) btnText.textContent = v.available ? this.data.strings.add : this.data.strings.soldOut;
      }
      if (v.featured_media) {
        const g = s.querySelector('apl-gallery');
        g && g.showMedia && g.showMedia(v.featured_media);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    }
    money(cents) {
      const fmt = this.data.moneyFormat || '${{amount}}';
      const n = (cents / 100).toFixed(2);
      const withCommas = n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fmt
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas.split('.')[0])
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, n.replace('.', ','))
        .replace(/\{\{\s*amount\s*\}\}/, withCommas)
        .replace(/<[^>]*>/g, '');
    }
  }
  if (!customElements.get('apl-variant-picker')) customElements.define('apl-variant-picker', AplVariantPicker);

  /* ---------- Animated story: play once when scrolled into view, with replay ---------- */
  class AplStory extends HTMLElement {
    connectedCallback() {
      this.replayBtn = this.querySelector('[data-st-replay]');
      this.loop = this.hasAttribute('data-loop');
      const last = this.querySelector('.st-route');
      if (last) last.addEventListener('animationend', () => {
        if (this.loop) { this.loopTimer = setTimeout(() => this.replay(), 1800); }
        else if (this.replayBtn) this.replayBtn.hidden = false;
      });
      if (this.replayBtn) this.replayBtn.addEventListener('click', () => this.replay());
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) this.loop = false;
      if (!('IntersectionObserver' in window)) { this.play(); return; }
      const eager = this.dataset.autoplay === 'load';
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { this.play(); this.io.disconnect(); } });
      }, { threshold: eager ? 0.12 : 0.45 });
      this.io.observe(this);
    }
    disconnectedCallback() { clearTimeout(this.loopTimer); if (this.io) this.io.disconnect(); }
    play() { this.classList.add('is-playing'); }
    replay() {
      if (this.replayBtn) this.replayBtn.hidden = true;
      this.classList.remove('is-playing');
      this.classList.add('is-reset');
      void this.offsetWidth;
      this.classList.remove('is-reset');
      setTimeout(() => this.play(), 30);
    }
  }
  if (!customElements.get('apl-story')) customElements.define('apl-story', AplStory);

  /* ---------- Mix-and-match bundle builder ---------- */
  class AplMix extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const cfg = this.querySelector('[data-mix-config]');
      if (!cfg) return;
      this.cfg = JSON.parse(cfg.textContent);
      this.rows = Array.from(this.querySelectorAll('[data-mix-row]')).map((el) => ({
        el, id: Number(el.dataset.variantId), title: el.dataset.title,
        qtyEl: el.querySelector('[data-mix-qty]'), qty: 0,
      }));
      this.packs = Array.from(this.querySelectorAll('input[type=radio][data-pct]'));
      this.modes = Array.from(this.querySelectorAll('.apl-mix__modes input[type=radio]'));
      this.split = this.querySelector('[data-mix-split]');
      this.mixWrap = this.querySelector('[data-mode-mix-wrap]');
      this.firstQty = 1; // quantity of the first variant when mixing

      this.packs.forEach((p) => p.addEventListener('change', () => this.update()));
      this.modes.forEach((m) => m.addEventListener('change', () => {
        if (m.checked && m.value === 'mix') this.firstQty = Math.max(1, Math.ceil(this.pack() / 2));
        // Show the chosen variant's own photo (e.g. the white tracker) when it has one.
        const g = (this.closest('section') || document).querySelector('apl-gallery');
        if (m.checked && m.dataset.media && g && g.showMedia) g.showMedia(m.dataset.media);
        this.update();
      }));
      this.rows.forEach((r, i) => {
        r.el.querySelector('[data-mix-inc]').addEventListener('click', () => this.step(i, +1));
        r.el.querySelector('[data-mix-dec]').addEventListener('click', () => this.step(i, -1));
      });
      this.querySelector('[data-mix-add]').addEventListener('click', () => this.add());

      const params = new URLSearchParams(window.location.search);
      const want = params.get('pack');
      if (want) { const p = this.packs.find((x) => x.value === want); if (p) p.checked = true; }
      this.update();
    }
    pack() { const p = this.packs.find((x) => x.checked); return p ? Number(p.value) : 1; }
    pct() { const p = this.packs.find((x) => x.checked); return p ? Number(p.dataset.pct) : 0; }
    mode() { const m = this.modes.find((x) => x.checked); return m ? m.value : '0'; }
    step(i, delta) {
      const n = this.pack();
      // Row 0 holds firstQty, row 1 holds the rest; both stay at least 1 while mixing.
      const next = i === 0 ? this.firstQty + delta : this.firstQty - delta;
      if (next < 1 || next > n - 1) return;
      this.firstQty = next;
      this.update();
    }
    money(cents) {
      const fmt = this.cfg.moneyFormat || '${{amount}}';
      const n = (cents / 100).toFixed(2);
      const withCommas = n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fmt.replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas.split('.')[0])
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, n.replace('.', ','))
        .replace(/\{\{\s*amount\s*\}\}/, withCommas).replace(/<[^>]*>/g, '');
    }
    update() {
      const n = this.pack();
      const pct = this.pct();
      // Mixing needs at least 2 trackers.
      const canMix = n >= 2 && this.rows.length >= 2;
      if (this.mixWrap) this.mixWrap.hidden = !canMix;
      let mode = this.mode();
      if (mode === 'mix' && !canMix) { this.modes[0].checked = true; mode = '0'; }

      // Labels: "Apple" for a single tracker, "All Apple" for a pack.
      this.querySelectorAll('[data-mode-label]').forEach((el) => { el.textContent = n > 1 ? el.dataset.all : el.dataset.single; });

      if (mode === 'mix') {
        this.firstQty = Math.min(Math.max(1, this.firstQty), n - 1);
        this.rows[0].qty = this.firstQty;
        this.rows[1].qty = n - this.firstQty;
      } else {
        this.rows.forEach((r, i) => { r.qty = String(i) === mode ? n : 0; });
      }
      if (this.split) this.split.hidden = mode !== 'mix';
      this.rows.forEach((r) => {
        r.qtyEl.textContent = r.qty;
        r.el.querySelector('[data-mix-inc]').disabled = r.qty >= n - 1;
        r.el.querySelector('[data-mix-dec]').disabled = r.qty <= 1;
      });

      const full = this.cfg.unitPrice * n;
      const price = Math.round(full * (100 - pct) / 100);
      const priceEl = this.querySelector('[data-apl-price]');
      if (priceEl) priceEl.textContent = this.money(price);
      const was = this.querySelector('[data-mix-was]');
      if (was) { was.hidden = pct === 0; was.textContent = pct ? this.money(full) : ''; }
      const sel = this.querySelector('[data-apl-selected]');
      if (sel) sel.textContent = this.rows.filter((r) => r.qty > 0).map((r) => r.qty + ' × ' + r.title).join(' + ') + (pct ? ' · ' + (((this.packs.find((x) => x.checked) || {}).dataset || {}).label || pct + '% off') : '');
      document.querySelectorAll('[data-apl-price-bar]').forEach((el) => { el.textContent = this.money(price); });
    }
    async add() {
      const btn = this.querySelector('[data-mix-add]');
      const text = btn.querySelector('[data-apl-add-text]');
      const err = this.querySelector('[data-mix-error]');
      const spinner = btn.querySelector('.apl-buy__btn-spinner');
      const n = this.pack();
      const items = this.rows.filter((r) => r.qty > 0).map((r) => ({
        id: r.id, quantity: r.qty, properties: n > 1 ? { _bundle: n + ' Pack' } : {},
      }));
      if (!items.length) return;
      const drawer = document.querySelector('cart-drawer');
      const body = { items };
      if (drawer && drawer.getSectionsToRender) {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }
      err.hidden = true;
      btn.disabled = true; btn.classList.add('loading'); if (spinner) spinner.classList.remove('hidden');
      text.textContent = this.cfg.strings.adding;
      try {
        const res = await fetch(this.cfg.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || this.cfg.strings.error);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'apl-mix', cartData: data });
        if (drawer && data.sections) {
          drawer.classList.remove('is-empty');
          if (drawer.setActiveElement) drawer.setActiveElement(btn);
          drawer.renderContents(data);
        } else {
          window.location = this.cfg.cartUrl;
        }
      } catch (e) {
        err.textContent = e.message || this.cfg.strings.error; err.hidden = false;
      } finally {
        btn.disabled = false; btn.classList.remove('loading'); if (spinner) spinner.classList.add('hidden');
        text.textContent = this.cfg.strings.add;
      }
    }
  }
  if (!customElements.get('apl-mix')) customElements.define('apl-mix', AplMix);

  /* Pack + colour picker (snippet apl-packs): one colour choice per item in the pack.
     Reuses AplMix pricing and add-to-cart; rows are rebuilt from the chosen colours. */
  class AplPacks extends AplMix {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const cfg = this.querySelector('[data-mix-config]');
      if (!cfg) return;
      this.cfg = JSON.parse(cfg.textContent);
      this.rows = [];
      const vj = this.querySelector('[data-packs-variants]');
      this.variants = vj ? JSON.parse(vj.textContent) : [];
      this.packs = Array.from(this.querySelectorAll('input[type=radio][data-pct]'));
      this.slots = Array.from(this.querySelectorAll('[data-slot]'));
      this.packs.forEach((p) => p.addEventListener('change', () => this.update()));
      this.slots.forEach((s) => s.querySelectorAll('input').forEach((i) => i.addEventListener('change', () => { this.update(); this.showSlotImage(s); })));
      this.querySelector('[data-mix-add]').addEventListener('click', () => this.add());
      const want = new URLSearchParams(window.location.search).get('pack');
      if (want) { const p = this.packs.find((x) => x.value === want); if (p) p.checked = true; }
      this.update();
    }
    update() {
      const n = this.pack();
      const pct = this.pct();
      const tally = new Map();
      let missing = false;
      this.slots.forEach((s, i) => {
        s.hidden = i >= n;
        // Each slot picks one value per option; the matching variant is the item added to the bag.
        const picked = [];
        s.querySelectorAll('input[data-opt]:checked').forEach((c) => { picked[Number(c.dataset.opt)] = c.value; });
        const v = this.variants.find((x) => x.options.every((o, k) => o === picked[k]));
        const label = s.querySelector('[data-slot-value]');
        if (!v || !v.available) { label.textContent = picked.join(' · ') + ' (unavailable)'; if (i < n) missing = true; return; }
        label.textContent = picked.join(' · ');
        if (i >= n) return;
        const row = tally.get(v.id) || { id: v.id, title: picked.join(' · '), qty: 0 };
        row.qty += 1;
        tally.set(v.id, row);
      });
      this.rows = Array.from(tally.values());
      const addBtn = this.querySelector('[data-mix-add]');
      if (addBtn) addBtn.disabled = missing;
      const full = this.cfg.unitPrice * n;
      const price = Math.round(full * (100 - pct) / 100);
      const priceEl = this.querySelector('[data-apl-price]');
      if (priceEl) priceEl.textContent = this.money(price);
      const was = this.querySelector('[data-mix-was]');
      if (was) { was.hidden = pct === 0; was.textContent = pct ? this.money(full) : ''; }
      const sel = this.querySelector('[data-apl-selected]');
      if (sel) sel.textContent = this.rows.map((r) => r.qty + ' × ' + r.title).join(' + ') + (pct ? ' · ' + (((this.packs.find((x) => x.checked) || {}).dataset || {}).label || pct + '% off') : '');
      document.querySelectorAll('[data-apl-price-bar]').forEach((el) => { el.textContent = this.money(price); });
    }
  }
  // Show the photo of the combination just picked (the variant's own image), if it has one.
  AplPacks.prototype.showSlotImage = function (slot) {
    const picked = [];
    slot.querySelectorAll('input[data-opt]:checked').forEach((c) => { picked[Number(c.dataset.opt)] = c.value; });
    const v = this.variants.find((x) => x.options.every((o, k) => o === picked[k]));
    const scope = this.closest('section') || document;
    const gallery = scope.querySelector('apl-gallery') || document.querySelector('apl-gallery');
    if (v && v.media && gallery && gallery.showMedia) gallery.showMedia(v.media);
  };
  if (!customElements.get('apl-packs')) customElements.define('apl-packs', AplPacks);

  /* ---------- Mobile sticky buy bar ---------- */
  function initBuyBar(root) {
    (root || document).querySelectorAll('[data-apl-buy-bar]').forEach((bar) => {
      if (bar.dataset.ready) return;
      bar.dataset.ready = '1';
      const main = document.querySelector('[data-apl-add]');
      const btn = bar.querySelector('[data-apl-bar-add]');
      if (!main || !('IntersectionObserver' in window)) return;
      btn.addEventListener('click', () => main.click());
      new IntersectionObserver(([e]) => {
        const show = !e.isIntersecting && e.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        btn.tabIndex = show ? 0 : -1;
      }).observe(main);
      const sync = () => {
        const p = document.querySelector('.apl-buy__summary [data-apl-price]');
        const t = bar.querySelector('[data-apl-price-bar]');
        if (p && t) t.textContent = p.textContent;
        btn.disabled = main.disabled;
      };
      document.addEventListener('change', sync);
    });
  }

  /* ---------- Express processing upsell (cart drawer + cart page) ---------- */
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-apl-express] input[type=checkbox]');
    if (!input) return;
    const box = input.closest('[data-apl-express]');
    const drawer = box.closest('cart-drawer') || null;
    const routes = window.routes || {};
    const body = {};
    if (drawer && drawer.getSectionsToRender) {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    let url;
    if (input.checked) {
      url = (routes.cart_add_url || '/cart/add') + '.js';
      body.items = [{ id: Number(box.dataset.variantId), quantity: 1 }];
    } else {
      url = (routes.cart_change_url || '/cart/change') + '.js';
      body.id = box.dataset.lineKey || String(box.dataset.variantId);
      body.quantity = 0;
    }
    input.disabled = true;
    box.classList.add('is-busy');
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || 'Cart error');
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'apl-express', cartData: data });
      if (drawer && data.sections) {
        drawer.renderContents(data);
      } else {
        window.location.reload();
      }
    } catch (err) {
      input.checked = !input.checked;
      input.disabled = false;
      box.classList.remove('is-busy');
    }
  });

  /* ---------- Discount quiz popup ---------- */
  class AplQuiz extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.store = {
        get: (k) => { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
        set: (k, v) => { try { window.localStorage.setItem(k, v); } catch (e) {} },
        sget: (k) => { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } },
        sset: (k, v) => { try { window.sessionStorage.setItem(k, v); } catch (e) {} },
      };
      this.state = { phone: '', track: [] };
      try { Object.assign(this.state, JSON.parse(this.store.sget('aplQuizState') || '{}')); } catch (e) {}
      this.teaser = document.querySelector('[data-quiz-teaser]');
      this.dialog = this.querySelector('.apl-quiz__dialog');

      this.querySelectorAll('[data-quiz-close]').forEach((b) => b.addEventListener('click', () => this.close(true)));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(true); });
      this.querySelectorAll('[data-phone]').forEach((b) => b.addEventListener('click', () => {
        this.state.phone = b.dataset.phone;
        this.querySelectorAll('[data-phone]').forEach((x) => x.classList.toggle('is-selected', x === b));
        setTimeout(() => this.go(2), 180);
      }));
      const next = this.querySelector('[data-quiz-next]');
      this.querySelectorAll('[data-track]').forEach((c) => c.addEventListener('click', () => {
        const v = c.dataset.track;
        const on = !this.state.track.includes(v);
        this.state.track = on ? this.state.track.concat(v) : this.state.track.filter((x) => x !== v);
        c.classList.toggle('is-selected', on);
        c.setAttribute('aria-pressed', String(on));
        next.disabled = this.state.track.length === 0;
      }));
      next.addEventListener('click', () => this.go(3));
      this.querySelectorAll('[data-quiz-back]').forEach((b) => b.addEventListener('click', () => this.go(this.step - 1)));

      const form = this.querySelector('form');
      if (form) form.addEventListener('submit', (e) => {
        const name = this.querySelector('[data-quiz-name]');
        const email = this.querySelector('[data-quiz-email]');
        const ok = name.value.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim());
        this.querySelector('[data-quiz-error]').hidden = !!ok;
        if (!ok) { e.preventDefault(); return; }
        const tags = ['newsletter', 'quiz'];
        if (this.state.phone) tags.push('quiz-phone-' + this.state.phone);
        this.state.track.forEach((t) => tags.push('quiz-track-' + t));
        this.querySelector('[data-quiz-tags]').value = tags.join(',');
        this.state.name = name.value.trim();
        this.store.sset('aplQuizState', JSON.stringify(this.state));
        this.store.sset('aplQuizSubmitted', '1');
      });
      const copy = this.querySelector('[data-quiz-copy]');
      if (copy) copy.addEventListener('click', () => {
        const code = this.dataset.code;
        const done = () => { copy.textContent = 'Copied'; setTimeout(() => { copy.textContent = 'Copy'; }, 1800); };
        if (navigator.clipboard) navigator.clipboard.writeText(code).then(done, done); else done();
      });
      if (this.teaser) this.teaser.addEventListener('click', () => this.open(this.step && this.step < 4 ? this.step : 1));

      // After a successful signup the page reloads with ?customer_posted=true.
      const posted = /[?&]customer_posted=true/.test(window.location.search);
      if (posted && this.store.sget('aplQuizSubmitted')) {
        this.store.set('aplQuizDone', '1');
        this.store.sset('aplQuizSubmitted', '');
        this.showSuccess();
        return;
      }
      if (this.querySelector('[data-quiz-server-error]') && this.store.sget('aplQuizSubmitted')) { this.open(3); return; }
      if (this.hasAttribute('data-preview')) { this.open(1); return; }
      if (this.store.get('aplQuizDone')) return;
      const dismissed = Number(this.store.get('aplQuizDismissed') || 0);
      if (dismissed && Date.now() - dismissed < 7 * 864e5) { this.showTeaser(); return; }
      this.arm();
    }
    arm() {
      let fired = false;
      const fire = () => {
        if (fired) return; fired = true;
        clearTimeout(timer); window.removeEventListener('scroll', onScroll); document.removeEventListener('mouseout', onOut);
        if (document.querySelector('cart-drawer.active, .drawer.active')) { this.showTeaser(); return; }
        this.open(1);
      };
      const timer = setTimeout(fire, (Number(this.dataset.delay) || 6) * 1000);
      const onScroll = () => { if (window.scrollY > document.documentElement.scrollHeight * 0.45) fire(); };
      const onOut = (e) => { if (!e.relatedTarget && e.clientY <= 0) fire(); };
      window.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('mouseout', onOut);
    }
    open(step) {
      this.hidden = false;
      document.documentElement.classList.add('apl-quiz-open');
      if (this.teaser) this.teaser.hidden = true;
      requestAnimationFrame(() => this.classList.add('is-open'));
      this.go(step || 1);
      this.lastFocus = document.activeElement;
      setTimeout(() => this.dialog.focus(), 50);
    }
    close(dismissed) {
      this.classList.remove('is-open');
      document.documentElement.classList.remove('apl-quiz-open');
      setTimeout(() => { this.hidden = true; }, 250);
      if (dismissed && this.step !== 4) { this.store.set('aplQuizDismissed', String(Date.now())); this.showTeaser(); }
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }
    showTeaser() { if (this.teaser && !this.store.get('aplQuizDone')) this.teaser.hidden = false; }
    go(step) {
      step = Math.max(1, Math.min(4, step));
      this.step = step;
      this.querySelectorAll('[data-step]').forEach((s) => s.classList.toggle('is-active', Number(s.dataset.step) === step));
      const bar = this.querySelector('[data-quiz-bar]');
      if (bar) bar.style.width = Math.min(100, (step / 3) * 100) + '%';
      if (step === 3) {
        const pitch = this.querySelector('[data-quiz-pitch]');
        const labels = Array.from(this.querySelectorAll('[data-track].is-selected')).map((c) => c.textContent.trim().toLowerCase());
        if (pitch && labels.length) pitch.textContent = 'Great for your ' + this.list(labels.slice(0, 3)) + '. We’ll reveal your discount instantly and email it to you too.';
        const nm = this.querySelector('[data-quiz-name]');
        if (nm) setTimeout(() => nm.focus(), 60);
      }
    }
    list(a) { return a.length < 2 ? a.join('') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1]; }
    showSuccess() {
      this.open(4);
      const t = this.querySelector('[data-quiz-thanks]');
      if (t && this.state.name) t.textContent = this.state.name + ', you’ve unlocked ' + (t.dataset.offer || 'your discount') + '!';
      const reco = this.querySelector('[data-quiz-reco]');
      if (reco) {
        const n = this.state.track.length;
        const phone = this.state.phone === 'both' ? 'a mix of Apple and Android trackers' : (this.state.phone === 'android' ? 'Android trackers' : 'Apple trackers');
        reco.textContent = n >= 3 ? 'Tip: a 4 Pack of ' + phone + ' saves you 30%.' : (n === 2 ? 'Tip: a 2 Pack of ' + phone + ' saves you 15%.' : '');
      }
    }
  }
  if (!customElements.get('apl-quiz')) customElements.define('apl-quiz', AplQuiz);

  /* ---------- Boot ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { initReveal(); initBuyBar(); });
  } else {
    initReveal();
    initBuyBar();
  }
  document.addEventListener('shopify:section:load', (e) => { initReveal(e.target); initBuyBar(e.target); });
})();

/* Shop catalog: colour swatches preview that colour's photo without leaving the page. */
document.addEventListener('click', (e) => {
  const sw = e.target.closest('.apl-catalog__swatch');
  if (!sw) return;
  e.preventDefault();
  e.stopPropagation();
  const card = sw.closest('.apl-catalog__card');
  const img = card && card.querySelector('.apl-catalog__media img');
  if (img && sw.dataset.catImg) { img.removeAttribute('srcset'); img.src = sw.dataset.catImg; }
  card.querySelectorAll('.apl-catalog__swatch').forEach((b) => b.classList.toggle('is-active', b === sw));
});

/* ---------- Mobile sticky Add to Bag ----------
   On phones, a slim bar with price and Add to Bag appears once the main button scrolls off the top.
   Its button clicks the real button, so packs, colours and the cart drawer all behave the same. */
(function () {
  const init = () => {
    const buy = document.querySelector('[data-apl-buy]');
    const btn = buy && buy.querySelector('[data-apl-add]');
    if (!btn || document.querySelector('.apl-sticky-buy')) return;
    // The price next to Add to Bag (pack total), not the single-item price at the top.
    const summary = btn.closest('.apl-buy__summary');
    const priceEl = (summary && summary.querySelector('[data-apl-price]')) || buy.querySelector('[data-apl-price]');
    const titleEl = buy.querySelector('.apl-buy__title');
    const bar = document.createElement('div');
    bar.className = 'apl-sticky-buy';
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML = '<div class="apl-sticky-buy__thumbs"></div><div class="apl-sticky-buy__info"><span class="apl-sticky-buy__name"></span><strong class="apl-sticky-buy__price"></strong></div><button type="button" class="apl-btn apl-btn--sm" tabindex="-1"></button>';
    const baseName = titleEl ? titleEl.textContent.replace(/^\s*Buy\s+/i, '').replace(/^ROVEN360\s+/i, '').trim() : '';
    const thumbs = bar.querySelector('.apl-sticky-buy__thumbs');
    const sbtn = bar.querySelector('button');
    const sync = () => {
      bar.querySelector('.apl-sticky-buy__price').textContent = priceEl ? priceEl.textContent.trim() : '';
      const t = btn.querySelector('[data-apl-add-text]');
      sbtn.textContent = (t ? t.textContent : btn.textContent).trim();
      sbtn.disabled = btn.disabled;
      // Mirror the shopper's selection: pack size in the name, and the "Your pick" thumbnails.
      const packEl = buy.querySelector('input[name$="-pack"]:checked');
      const pack = packEl ? Number(packEl.value) : 1;
      bar.querySelector('.apl-sticky-buy__name').textContent = baseName + (pack > 1 ? ' · ' + pack + '-pack' : '');
      const picks = Array.from(buy.querySelectorAll('.apl-pick__item'));
      thumbs.innerHTML = '';
      picks.slice(0, 3).forEach((it) => thumbs.appendChild(it.cloneNode(true)));
      if (picks.length > 3) {
        const more = document.createElement('span');
        more.className = 'apl-sticky-buy__more';
        more.textContent = '+' + (picks.length - 3);
        thumbs.appendChild(more);
      }
      thumbs.hidden = picks.length === 0;
    };
    sync();
    document.addEventListener('apl:pick', sync);
    const mo = new MutationObserver(sync);
    if (priceEl) mo.observe(priceEl, { childList: true, characterData: true, subtree: true });
    mo.observe(btn, { attributes: true, childList: true, characterData: true, subtree: true });
    sbtn.addEventListener('click', () => btn.click());
    document.body.appendChild(bar);
    let ticking = false;
    const update = () => {
      ticking = false;
      const show = btn.getBoundingClientRect().bottom < 0;
      bar.classList.toggle('is-shown', show);
      document.documentElement.classList.toggle('has-sticky-buy', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      sbtn.tabIndex = show ? 0 : -1;
    };
    const queue = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ---------- Mobile "Your pick" preview ----------
   On phones the gallery is out of view while choosing colours, so a small strip above the options
   shows the photo for each selected item (one per pack item, grouped with a ×n badge). */
(function () {
  const init = () => {
    const buy = document.querySelector('[data-apl-buy]');
    const picker = buy && buy.querySelector('apl-packs, apl-mix');
    const gallery = buy && buy.querySelector('apl-gallery');
    if (!picker || !gallery || picker.querySelector('.apl-pick')) return;

    // media id -> small image URL, taken from the gallery (includes option-only photos)
    const srcFor = {};
    gallery.querySelectorAll('[data-slide][data-media-id]').forEach((s) => {
      const img = s.querySelector('img');
      if (!img) return;
      const set = (img.getAttribute('srcset') || '').split(',').map((p) => p.trim().split(' ')[0]).filter(Boolean);
      srcFor[s.dataset.mediaId] = set[0] || img.currentSrc || img.src;
    });
    const firstSlide = gallery.querySelector('[data-slide] img');
    const fallback = firstSlide ? (firstSlide.getAttribute('srcset') || '').split(',')[0].trim().split(' ')[0] || firstSlide.src : '';
    const packsJson = picker.querySelector('[data-packs-variants]');
    const variants = packsJson ? JSON.parse(packsJson.textContent) : [];
    const modes = Array.from(picker.querySelectorAll('input[name$="-mode"]'));

    const box = document.createElement('div');
    box.className = 'apl-pick';
    box.setAttribute('aria-hidden', 'true');
    box.innerHTML = '<span class="apl-pick__label">Your pick</span><div class="apl-pick__items"></div>';
    const anchor = picker.querySelector('.apl-packs__slots, .apl-mix__modes') || picker.firstElementChild;
    anchor.parentNode.insertBefore(box, anchor);
    const list = box.querySelector('.apl-pick__items');

    const selection = () => {
      const items = [];
      const add = (media, title) => {
        const key = media + '|' + title;
        const hit = items.find((x) => x.key === key);
        if (hit) hit.n += 1; else items.push({ key, src: srcFor[media] || fallback, title, n: 1 });
      };
      if (packsJson) {
        picker.querySelectorAll('[data-slot]').forEach((slot) => {
          if (slot.hidden) return;
          const picked = [];
          slot.querySelectorAll('input[data-opt]:checked').forEach((c) => { picked[Number(c.dataset.opt)] = c.value; });
          const v = variants.find((x) => x.options.every((o, k) => o === picked[k]));
          if (v) add(String(v.media), v.title);
        });
      } else {
        const packEl = picker.querySelector('input[name$="-pack"]:checked');
        const pack = packEl ? Number(packEl.value) : 1;
        const mode = modes.find((m) => m.checked);
        const split = picker.querySelector('[data-mix-split]');
        if (mode && mode.value === 'mix' && split && !split.hidden) {
          picker.querySelectorAll('[data-mix-row]').forEach((row, i) => {
            const n = Number((row.querySelector('[data-mix-qty]') || {}).textContent || 0);
            const m = modes[i] && modes[i].dataset.media;
            for (let k = 0; k < n; k++) add(String(m), row.dataset.title || '');
          });
        } else {
          const label = mode ? (mode.closest('.apl-tile-opt') || mode.parentNode).textContent.trim() : '';
          for (let k = 0; k < pack; k++) add(String(mode && mode.dataset.media), label);
        }
      }
      return items;
    };
    const render = () => {
      const items = selection();
      list.innerHTML = '';
      items.forEach((it) => {
        const el = document.createElement('span');
        el.className = 'apl-pick__item';
        el.title = it.title;
        el.innerHTML = '<img alt="" decoding="async">' + (it.n > 1 ? '<em>×' + it.n + '</em>' : '');
        el.querySelector('img').src = it.src;
        list.appendChild(el);
      });
      box.hidden = items.length === 0;
      document.dispatchEvent(new CustomEvent('apl:pick'));
    };
    let t = null;
    const queue = () => { clearTimeout(t); t = setTimeout(render, 30); };
    picker.addEventListener('change', queue);
    picker.addEventListener('click', queue);
    render();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
